/**
 * Library administration — create rooms, set capacity, grant mentors access.
 *
 * Reachable by ADMIN and COORDINATOR. Running a reading room is day-to-day
 * coordination; the Accountant is deliberately excluded, since a library is
 * not a payment surface. That is decided server-side in `ADMIN_AREA_GRANTS`
 * — this screen only avoids showing a door that will not open.
 */

import { useState } from 'react'
import {
  Callout,
  Card,
  PageHeader,
  PageStack,
} from '@/components/ui/Page'
import { Button } from '@/components/ui/Button'
import { Modal } from '@/components/ui/Modal'
import { LibraryMentorsModal } from './LibraryMentorsModal'
import { Pill } from '@/components/ui/Pill'
import { Table } from '@/components/ui/Table'
import { SelectField, TextAreaField, TextField } from '@/components/ui/Field'
import { useToast } from '@/components/ui/Toast'
import { ApiError } from '@/lib/api'
import {
  useAdminLibraries,
  useCloseLibrary,
  useCreateLibrary,
  useUpdateLibrary,
} from '@/lib/queries'
import type { AdminLibrary, LibraryInput } from '@/types/api'
import styles from './AdminLibrariesPage.module.css'

const ACCESS_OPTIONS = [
  { value: 'FREE', label: 'Free — any enrolled student' },
  { value: 'PLAN', label: 'Included in a subscription plan' },
  { value: 'PAID', label: 'Bought separately' },
]

/** HTML time inputs give "HH:MM"; the API stores TIME. Empty means unset. */
function timeOrNull(value: FormDataEntryValue | null): string | null {
  const text = String(value ?? '').trim()
  return text === '' ? null : text
}

export function AdminLibrariesPage() {
  /*
    *** ITS OWN STATE, NOT THE EDIT TARGET ***
    Reusing `editing` would open the edit form and the mentor list from
    the same click, and closing one would close the other.
  */
  const [mentorsFor, setMentorsFor] = useState<AdminLibrary | null>(null)
  const { data, isLoading, error, refetch } = useAdminLibraries()
  const create = useCreateLibrary()
  const update = useUpdateLibrary()
  const close = useCloseLibrary()
  const toast = useToast()

  const [creating, setCreating] = useState(false)
  const [editing, setEditing] = useState<AdminLibrary | null>(null)

  const libraries = data?.libraries ?? []

  /*
    A room students cannot enter is worth surfacing: an admin who set PLAN
    access before any plan includes the library has made a room nobody can
    use, and the only symptom is silence.
  */
  const planRooms = libraries.filter(
    (l) => l.is_active && l.access_mode === 'PLAN',
  )

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    const form = new FormData(event.currentTarget)

    const opens = timeOrNull(form.get('opens_at'))
    const closes = timeOrNull(form.get('closes_at'))

    /*
      Caught here as well as server-side. The API refuses it either way; this
      just means the admin is told before the round trip rather than after.
    */
    if ((opens === null) !== (closes === null)) {
      toast.error('Set both an opening and a closing time, or neither.')
      return
    }

    const input: LibraryInput = {
      name: String(form.get('name') ?? '').trim(),
      description: String(form.get('description') ?? '').trim() || null,
      capacity: Number(form.get('capacity') ?? 0),
      access_mode: String(form.get('access_mode') ?? 'FREE') as
        LibraryInput['access_mode'],
      opens_at: opens,
      closes_at: closes,
      display_order: Number(form.get('display_order') ?? 0),
    }

    try {
      if (editing) {
        await update.mutateAsync({ id: editing.library_id, input })
        toast.success(`${input.name} updated.`)
      } else {
        await create.mutateAsync(input)
        toast.success(`${input.name} created.`)
      }
      setCreating(false)
      setEditing(null)
    } catch (err) {
      toast.error(
        err instanceof ApiError ? err.message : 'Could not save the library.',
      )
    }
  }

  const handleClose = async (library: AdminLibrary) => {
    try {
      await close.mutateAsync(library.library_id)
      toast.success(`${library.name} closed to new arrivals.`)
    } catch (err) {
      toast.error(
        err instanceof ApiError ? err.message : 'Could not close the library.',
      )
    }
  }

  const columns = [
    {
      key: 'name',
      header: 'Library',
      render: (row: AdminLibrary) => (
        <div>
          <div className={styles.name}>{row.name}</div>
          {row.description && (
            <div className={styles.sub}>{row.description}</div>
          )}
        </div>
      ),
    },
    {
      key: 'occupancy',
      header: 'Inside now',
      render: (row: AdminLibrary) => (
        <div>
          <span className="numeric">
            {row.students_inside} / {row.capacity}
          </span>
          {/* Mentors are shown apart, never folded into the figure above:
              they do not consume a seat, so adding them would misreport how
              full the room is. */}
          <div className={styles.sub}>
            {row.mentors_inside > 0
              ? `${row.mentors_inside} mentor${row.mentors_inside === 1 ? '' : 's'}`
              : 'no mentor'}
          </div>
        </div>
      ),
    },
    {
      key: 'access',
      header: 'Access',
      render: (row: AdminLibrary) => (
        <Pill tone={row.access_mode === 'FREE' ? 'neutral' : 'info'}>
          {row.access_mode === 'FREE'
            ? 'Free'
            : row.access_mode === 'PLAN'
              ? 'With a plan'
              : 'Paid'}
        </Pill>
      ),
    },
    {
      key: 'hours',
      header: 'Hours',
      secondary: true,
      render: (row: AdminLibrary) =>
        row.opens_at && row.closes_at ? (
          <span className="numeric">
            {row.opens_at.slice(0, 5)}–{row.closes_at.slice(0, 5)}
          </span>
        ) : (
          <span className={styles.sub}>Always open</span>
        ),
    },
    {
      key: 'status',
      header: 'Status',
      render: (row: AdminLibrary) =>
        row.is_active ? (
          <Pill tone="success">Open</Pill>
        ) : (
          <Pill tone="neutral">Closed</Pill>
        ),
    },
    {
      key: 'actions',
      header: '',
      align: 'right' as const,
      render: (row: AdminLibrary) => (
        <div className={styles.rowActions}>
          {/*
            *** THE ONLY WAY TO PUT A MENTOR IN A ROOM ***
            Without this the grant is SQL-only, and every library reads
            "no mentor is in right now" to the students sitting in it.
          */}
          <Button variant="secondary" onClick={() => setMentorsFor(row)}>
            Mentors
          </Button>
          <Button variant="secondary" onClick={() => setEditing(row)}>
            Edit
          </Button>
          {row.is_active && (
            <Button
              variant="secondary"
              onClick={() => handleClose(row)}
              disabled={close.isPending}
            >
              Close
            </Button>
          )}
        </div>
      ),
    },
  ]

  const target = editing

  return (
    <PageStack>
      <PageHeader
        title="Libraries"
        description="Virtual study rooms. Students sit in them together; mentors you grant access can join to answer doubts."
        actions={
          <Button variant="primary" onClick={() => setCreating(true)}>
            Add library
          </Button>
        }
      />

      {planRooms.length > 0 && (
        <Callout tone="info" title="Plan-based access">
          <p>
            {planRooms.map((l) => l.name).join(', ')}{' '}
            {planRooms.length === 1 ? 'is' : 'are'} open only to students on a
            plan that includes the library. Set that on the plan itself —
            until at least one plan includes it, no student can enter.
          </p>
        </Callout>
      )}

      <Card flush>
        <Table
          columns={columns}
          rows={libraries}
          rowKey={(row) => row.library_id}
          loading={isLoading}
          error={error}
          onRetry={() => void refetch()}
          emptyMessage="No libraries yet. Add one to let students start studying together."
        />
      </Card>

      <LibraryMentorsModal
        library={mentorsFor}
        onClose={() => setMentorsFor(null)}
      />

      <Modal
        open={creating || target !== null}
        onClose={() => {
          setCreating(false)
          setEditing(null)
        }}
        title={target ? 'Edit library' : 'Add library'}
        description={
          target
            ? undefined
            : 'A room students join to study alongside each other.'
        }
      >
        <form
          onSubmit={handleSubmit}
          className={styles.form}
          /* Remount on target change so defaultValue picks up the new row. */
          key={target?.library_id ?? 'new'}
        >
          <TextField
            label="Name"
            name="name"
            defaultValue={target?.name ?? ''}
            placeholder="e.g. Morning Hall"
            required
            autoFocus
            maxLength={150}
          />

          <TextAreaField
            label="Description"
            name="description"
            defaultValue={target?.description ?? ''}
            placeholder="Shown to students before they join."
            maxLength={1000}
          />

          <TextField
            label="Capacity"
            name="capacity"
            type="number"
            min={1}
            max={10000}
            defaultValue={String(target?.capacity ?? 50)}
            required
            hint="Students only — a mentor never takes a seat."
          />

          <SelectField
            label="Who can join"
            name="access_mode"
            options={ACCESS_OPTIONS}
            defaultValue={target?.access_mode ?? 'FREE'}
            hint="Free is the simplest, and the right choice while testing."
          />

          <div className={styles.times}>
            <TextField
              label="Opens at"
              name="opens_at"
              type="time"
              defaultValue={target?.opens_at?.slice(0, 5) ?? ''}
            />
            <TextField
              label="Closes at"
              name="closes_at"
              type="time"
              defaultValue={target?.closes_at?.slice(0, 5) ?? ''}
            />
          </div>
          <p className={styles.hint}>
            Leave both empty for a room that is always open. Times follow your
            institute&rsquo;s timezone.
          </p>

          <TextField
            label="Display order"
            name="display_order"
            type="number"
            min={0}
            defaultValue={String(target?.display_order ?? 0)}
            hint="Lower numbers appear first."
          />

          <div className={styles.formActions}>
            <Button
              variant="secondary"
              onClick={() => {
                setCreating(false)
                setEditing(null)
              }}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="primary"
              disabled={create.isPending || update.isPending}
            >
              {create.isPending || update.isPending
                ? 'Saving…'
                : target
                  ? 'Save changes'
                  : 'Create library'}
            </Button>
          </div>
        </form>
      </Modal>
    </PageStack>
  )
}
